import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { useDndMonitor, type Modifier } from '@dnd-kit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Task } from '../../types';
import { ProgressMarkers } from './ProgressMarkers';
import { SubtaskChip } from './SubtaskChip';
import { useElementWidth } from '../../hooks/useElementWidth';

/**
 * Below this lane width, subtask chips render in compact mode
 * (dot + truncated title + %) instead of the full title.
 *
 * Decoupled from the layout breakpoint: compact mode kicks in based on
 * actual lane width, so it activates correctly whether the lane was
 * shrunk by viewport size, by the timeline drawer covering part of the
 * track, or by a future layout change.
 */
export const COMPACT_LANE_WIDTH_THRESHOLD = 600;
import type { DragResult } from '../../hooks/useTrackDrag';
import { ProgressNotePopover } from './ProgressNotePopover';
import { updateTask, useDoneSubtaskCount, createTask } from '../../hooks/useTasks';
import { queryClient } from '../../api/queryClient';
import { useAppStore } from '../../store';
import { useToast } from '../Toast/ToastProvider';
import { Trophy, FolderOpen, CircleDot, Undo2, CheckCircle2, Trash2, Info, Ban, GripVertical, Plus, Zap, RefreshCw, Clock } from 'lucide-react';
import { useNearestFutureBlock } from '../../hooks/useTimelineBlocks';
import { format } from 'date-fns';
import { useContextMenu } from '../../hooks/useContextMenu';
import { deleteTask } from '../../hooks/useTasks';
import type { MenuAction } from '../shared/ContextMenu';

export function createHorizontalClampedModifier(trackRef: React.RefObject<HTMLDivElement | null>): Modifier {
  return ({ transform, active }) => {
    if (!trackRef.current || !active) return transform;
    const isSubtaskDrag = (active.data.current as { trackProgress?: number })?.trackProgress !== undefined;
    if (!isSubtaskDrag) return transform;
    const trackWidth = trackRef.current.getBoundingClientRect().width;
    const currentProgress = (active.data.current as { trackProgress?: number })?.trackProgress ?? 0;
    const currentPx = (currentProgress / 100) * trackWidth;
    const maxRight = trackWidth - currentPx;
    const maxLeft = -currentPx;
    // If dragged past the right edge of the track, release all clamps
    // so the chip can freely move toward the timeline
    if (transform.x > maxRight) {
      return transform; // Free movement — no clamping
    }

    const clampedX = Math.max(maxLeft, Math.min(maxRight, transform.x));
    return { ...transform, x: clampedX };
  };
}

interface RaceTrackProps {
  tasks: Task[];
  trackRef: React.RefObject<HTMLDivElement | null>;
  notePrompt: DragResult | null;
  setNotePrompt: (result: DragResult | null) => void;
  isDragging?: boolean;
  activeDragId?: string | null;
  nearDone?: boolean;
  onNearDoneChange?: (nearDone: boolean) => void;
  nearDoneRef?: React.MutableRefObject<boolean>;
}

export function RaceTrack({ tasks, trackRef, notePrompt, setNotePrompt, isDragging, activeDragId, nearDone = false, onNearDoneChange, nearDoneRef }: RaceTrackProps) {
  const { parentTasks, subtaskMap } = useMemo(() => {
    const parents = tasks
      .filter((t) => !t.parentId)
      .sort((a, b) => (a.trackOrder ?? 0) - (b.trackOrder ?? 0));
    const subs = new Map<string, Task[]>();
    for (const t of tasks) {
      if (t.parentId) {
        const list = subs.get(t.parentId) ?? [];
        list.push(t);
        subs.set(t.parentId, list);
      }
    }
    const priorityRank = { p1: 0, p2: 1, p3: 2 } as const;
    for (const [, list] of subs) {
      list.sort((a, b) => {
        const pa = priorityRank[a.priority] ?? 1;
        const pb = priorityRank[b.priority] ?? 1;
        if (pa !== pb) return pa - pb;
        return (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
      });
    }
    return { parentTasks: parents, subtaskMap: subs };
  }, [tasks]);

  const stableId = (t: Task) => t.templateId ?? t.id;
  const parentIds = parentTasks.map(stableId);

  const cardRef = useRef<HTMLDivElement>(null);
  const laneWidth = useElementWidth(trackRef);
  const isCompact = laneWidth !== null && laneWidth < COMPACT_LANE_WIDTH_THRESHOLD;

  const onDragMove = useCallback(({ active, activatorEvent, delta }: { active: any; activatorEvent: Event; delta: { x: number; y: number } }) => {
    const data = active.data.current as { trackProgress?: number } | undefined;
    if (data?.trackProgress === undefined || !trackRef.current || !cardRef.current) {
      onNearDoneChange?.(false);
      if (nearDoneRef) nearDoneRef.current = false;
      return;
    }
    const mouseEvent = activatorEvent as MouseEvent;
    const pointerX = mouseEvent.clientX + delta.x;
    const pointerY = mouseEvent.clientY + delta.y;
    const cardRect = cardRef.current.getBoundingClientRect();
    const trackRect = trackRef.current.getBoundingClientRect();
    const threshold95 = trackRect.left + trackRect.width * 0.95;
    const doneZoneRight = trackRect.right + 44;
    const inCardY = pointerY >= cardRect.top && pointerY <= cardRect.bottom;
    const isDone = inCardY && pointerX >= threshold95 && pointerX <= doneZoneRight;
    onNearDoneChange?.(isDone);
    if (nearDoneRef) nearDoneRef.current = isDone;
  }, [trackRef, nearDoneRef, onNearDoneChange]);

  useDndMonitor({
    onDragMove,
    onDragEnd() { onNearDoneChange?.(false); if (nearDoneRef) nearDoneRef.current = false; },
    onDragCancel() { onNearDoneChange?.(false); if (nearDoneRef) nearDoneRef.current = false; },
  });

  return (
    <>
      <div ref={cardRef} data-tutorial="race-track" className="surface-card flex flex-col relative overflow-visible">
        {/* Distance marker header */}
        <div className="sticky top-0 z-30 flex shrink-0 bg-bg-surface rounded-t-[10px]">
          <div className="w-64 shrink-0 border-r border-border-subtle border-b border-border-subtle" />
          <div className="flex-1 relative h-7 border-b border-border-subtle">
            <ProgressMarkers />
          </div>
          <div className="w-11 shrink-0 border-l border-border-subtle border-b border-border-subtle" />
        </div>

        {/* Track body */}
        <div className={`relative ${isDragging ? 'overflow-visible' : ''}`}>
          {/* Finish zone trophy overlay — scoped to the track body so the header divider stays visible */}
          <div className="absolute pointer-events-none flex flex-col items-center justify-center gap-1 transition-colors duration-200 z-20 rounded-br-[10px] border-l border-border-subtle bg-bg-surface" style={{ right: 0, top: 0, bottom: 0, width: '2.75rem' }}>
            <div className={`relative flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200 ${
              nearDone ? 'bg-finish/18 ring-2 ring-finish/45 shadow-lg shadow-finish/20' : ''
            }`}>
              {nearDone && (
                <span className="absolute inset-0 rounded-full bg-finish/15 animate-ping" />
              )}
              <Trophy size={18} className={`relative transition-transform duration-200 ${nearDone ? 'text-finish scale-125' : 'text-finish'}`} />
            </div>
            <span className={`text-label text-finish ${nearDone ? 'font-bold' : ''}`}>Done</span>
          </div>
          <SortableContext items={parentIds} strategy={verticalListSortingStrategy}>
            {parentTasks.map((task, i) => (
              <SortableTrackRow
                key={stableId(task)}
                task={task}
                index={i}
                subtasks={subtaskMap.get(task.id) ?? []}
                activeDragId={activeDragId}
                nearDone={nearDone}
                isCompact={isCompact}
              />
            ))}
          </SortableContext>
          {parentTasks.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
              className="flex flex-col items-center justify-center h-40 px-4 text-center gap-2"
            >
              <div className="w-10 h-10 rounded-xl bg-accent/8 flex items-center justify-center">
                <Zap size={18} className="text-accent/60" />
              </div>
              <p className="text-body font-medium text-text-muted">Empty track</p>
              <p className="text-caption text-text-muted/70">Add tasks or pull from the backlog to start your day</p>
            </motion.div>
          )}
          {/* Percentage guide lines spanning all rows */}
          <div className="absolute pointer-events-none" style={{ left: '16rem', right: '2.75rem', top: 0, bottom: 0 }}>
            {[25, 50, 75].map(pct => (
              <div key={pct} className="absolute top-0 bottom-0 border-l border-dashed border-track-marking" style={{ left: `${pct}%` }} />
            ))}
          </div>
          {/* Invisible element to measure track surface width for drag clamping */}
          <div ref={trackRef} className="absolute pointer-events-none" style={{ left: '16rem', right: '2.75rem', top: 0, height: 0 }} />
        </div>
      </div>

      {notePrompt && (
        <ProgressNotePopover
          taskId={notePrompt.taskId}
          taskTitle={notePrompt.taskTitle}
          progressBefore={notePrompt.progressBefore}
          progressAfter={notePrompt.progressAfter}
          onClose={() => setNotePrompt(null)}
        />
      )}
    </>
  );
}

interface SortableTrackRowProps {
  task: Task;
  index: number;
  subtasks: Task[];
  activeDragId?: string | null;
  nearDone?: boolean;
  isCompact?: boolean;
}

function SortableTrackRow({ task, index, subtasks, activeDragId, nearDone, isCompact }: SortableTrackRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.templateId ?? task.id,
    data: {
      taskId: task.id,
      title: task.title,
      priority: task.priority,
      itemType: task.itemType,
      trackProgress: task.trackProgress,
      parentId: task.parentId,
      canComplete: subtasks.length === 0,
    },
  });
  const completedSubtasks = useDoneSubtaskCount(task.id);
  const nearestBlock = useNearestFutureBlock(task.id);
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const localQueryClient = useQueryClient();
  const { addToast } = useToast();
  const totalSubs = subtasks.length + completedSubtasks;
  const laneMinHeight = Math.max(94, (subtasks.length + 1) * 44 + 6);
  const ctxMenu = useContextMenu();

  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPriority, setNewPriority] = useState<'p1' | 'p2' | 'p3'>('p2');

  const rowWrapperStyle: React.CSSProperties = {
    position: 'relative',
    zIndex: isDragging ? 40 : undefined,
  };

  const sortableStyle: React.CSSProperties = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)${isDragging ? ' scale(1.01)' : ''}` : isDragging ? 'scale(1.01)' : undefined,
    transition: isDragging
      ? undefined
      : transition ? 'transform 200ms cubic-bezier(0.22, 1, 0.36, 1)' : undefined,
    opacity: isDragging ? 0.96 : 1,
    transformOrigin: 'center',
    willChange: transform || isDragging ? 'transform' : undefined,
  };

  const progress = useMemo(() => {
    if (totalSubs === 0) return 0;
    const activeProgress = subtasks.reduce((sum, st) => sum + st.trackProgress, 0);
    const completedProgress = completedSubtasks * 100;
    return Math.round((activeProgress + completedProgress) / totalSubs);
  }, [subtasks, completedSubtasks, totalSubs]);

  const isProject = task.itemType === 'project';
  const isComplete = progress === 100 && (subtasks.length === 0 && completedSubtasks > 0);

  const handleMoveToBacklog = async () => {
    await updateTask(task.id, { status: 'backlog', trackDate: undefined });
    for (const st of subtasks) {
      await updateTask(st.id, { status: 'backlog', trackDate: undefined });
    }
    queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
    queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'backlog'] });
  };

  const completeMutation = useMutation({
    mutationFn: () =>
      updateTask(task.id, {
        status: 'done',
        trackProgress: 100,
        completedAt: new Date().toISOString(),
      }),
    onMutate: async () => {
      await localQueryClient.cancelQueries({ queryKey: ['tasks', spaceId, 'track'] });
      const previous = localQueryClient.getQueryData<Task[]>(['tasks', spaceId, 'track']);
      localQueryClient.setQueryData<Task[]>(['tasks', spaceId, 'track'], (old) =>
        old?.filter((t) => t.id !== task.id)
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        localQueryClient.setQueryData(['tasks', spaceId, 'track'], context.previous);
      }
      addToast("Couldn't mark as done — task restored", 'error');
    },
    onSettled: () => {
      localQueryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
    },
  });

  const handleComplete = () => {
    completeMutation.mutate();
  };

  const handleAddSubtask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    const maxOrder = subtasks.reduce((max, st) => Math.max(max, st.trackOrder ?? 0), -1);
    await createTask(newTitle.trim(), task.spaceId, newPriority, 'track', {
      parentId: task.id,
      trackDate: task.trackDate,
      trackProgress: 0,
      trackOrder: maxOrder + 1,
    });
    if (index === 0) {
      window.dispatchEvent(new CustomEvent('tutorial:subtask-created'));
    }
    setNewTitle('');
    setShowAdd(false);
  };

  useEffect(() => {
    if (index !== 0) return;
    const handler = () => setShowAdd(true);
    window.addEventListener('tutorial:open-add-subtask', handler);
    return () => window.removeEventListener('tutorial:open-add-subtask', handler);
  }, [index]);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const isRecurring = !!task.templateId;

    const items: MenuAction[] = [
      { label: 'View details', icon: <Info size={13} />, onClick: () => useAppStore.getState().setSelectedTaskId(task.id) },
    ];

    if (isRecurring) {
      items.push(
        { label: 'View template', icon: <RefreshCw size={13} />, onClick: () => useAppStore.getState().setSelectedTaskId(task.templateId!) },
        { label: 'Delete', icon: <Trash2 size={13} />, danger: true, onClick: async () => { await deleteTask(task.id); queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] }); } },
      );
    } else {
      items.push(
        { label: 'Move to backlog', icon: <Undo2 size={13} />, onClick: handleMoveToBacklog },
        { label: task.blocked ? 'Unblock' : 'Mark as blocked', icon: <Ban size={13} />, onClick: async () => { await updateTask(task.id, { blocked: !task.blocked }); queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] }); } },
        { label: totalSubs > 0 ? `Delete (${totalSubs} subtask${totalSubs > 1 ? 's' : ''})` : 'Delete', icon: <Trash2 size={13} />, danger: true, onClick: async () => { if (totalSubs > 0 && !confirm(`Delete "${task.title}" and its ${totalSubs} subtask(s)?`)) return; await deleteTask(task.id); queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] }); } },
      );
    }

    ctxMenu.open(e.clientX, e.clientY, items);
  };


  const [hasEntered, setHasEntered] = useState(false);

  return (
    <motion.div
      initial={!hasEntered ? { opacity: 0, x: -16 } : false}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.25, delay: index * 0.06, ease: [0.25, 1, 0.5, 1] }}
      onAnimationComplete={() => setHasEntered(true)}
      style={rowWrapperStyle}
    >
    <div
      ref={setNodeRef}
      style={sortableStyle}
      className={`flex border-b border-border-subtle ${
        isDragging
          ? 'bg-bg-surface shadow-2xl shadow-black/30 ring-1 ring-accent/35'
          : index % 2 === 0 ? 'bg-bg-surface/30' : ''
      }`}
      data-track-row
      {...(index === 0 ? { 'data-tutorial': 'first-track-task' } : {})}
      onContextMenu={handleContextMenu}
    >
      {/* Lane header */}
      <div className="w-64 shrink-0 border-r border-border-subtle bg-bg-app group flex flex-col px-3 py-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-text-muted hover:text-text-secondary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <GripVertical size={12} />
            </div>
            {isProject ? (
              <FolderOpen size={13} className="shrink-0" style={{ color: task.color ?? '#6366f1' }} />
            ) : (
              <CircleDot size={13} className="shrink-0 text-accent" />
            )}
            <button
              onClick={() => useAppStore.getState().setSelectedTaskId(task.id)}
              className="text-body font-semibold text-text-primary hover:text-accent transition-colors text-left truncate min-w-0"
              title={task.title}
            >
              {task.title}
            </button>
            {task.templateId && (
              <span title="Recurring task" className="shrink-0 text-accent/70">
                <RefreshCw size={11} />
              </span>
            )}
            {!task.templateId && (
              <button
                onClick={handleMoveToBacklog}
                className="p-0.5 rounded text-text-muted hover:text-p2 transition-colors opacity-0 group-hover:opacity-100 shrink-0 ml-auto"
                title="Move to backlog"
              >
                <Undo2 size={12} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {!!task.blocked && (
              <span className="text-label text-p1 bg-p1/10 px-1.5 py-0.5 rounded">Blocked</span>
            )}
            {totalSubs > 0 && <span className="text-micro text-text-muted">{totalSubs} sub</span>}
            <span className={`text-micro font-mono tabular-nums ${progress === 100 ? 'text-finish font-bold' : progress > 0 ? 'text-text-secondary font-semibold' : 'text-text-muted'}`}>{progress}%</span>
            {nearestBlock && (
              <span className="flex items-center gap-0.5 shrink-0">
                <Clock size={9} className="text-accent/70" />
                <span className="text-micro font-mono text-accent/70">
                  {nearestBlock.date !== format(new Date(), 'yyyy-MM-dd')
                    ? `${format(new Date(nearestBlock.date + 'T00:00:00'), 'MMM d')} `
                    : ''}{nearestBlock.scheduledStartTime}
                </span>
              </span>
            )}
            {isComplete && (
              <button
                onClick={handleComplete}
                className="flex items-center gap-1 text-micro font-semibold text-finish hover:text-finish/80 transition-colors ml-auto"
              >
                <CheckCircle2 size={11} />
                Done
              </button>
            )}
          </div>
          {/* Mini progress bar */}
          <div className="mt-1 h-1 bg-border-default/50 rounded-full overflow-hidden w-full">
            <div
              className={`h-full rounded-full transition-[width] duration-300 ease-out ${progress > 0 && progress < 100 ? 'progress-active' : ''}`}
              style={{
                width: `${Math.max(progress, 2)}%`,
                backgroundColor: progress === 100 ? 'var(--color-finish)' : (task.color ?? 'var(--color-accent)'),
                opacity: progress === 0 ? 0.3 : 1,
              }}
            />
          </div>
        </div>
      </div>

      {/* Track surface */}
      <div
        className={`flex-1 relative ${index % 2 === 0 ? 'track-lane-even' : 'track-lane-odd'}`}
        style={{ minHeight: `${laneMinHeight}px` }}
      >
        {subtasks.map((st, i) => (
          <div
            key={st.id}
            className="absolute left-0 right-0"
            style={{ top: `${i * 44 + 10}px`, height: '38px' }}
            {...(index === 0 && i === 0 ? { 'data-tutorial': 'first-subtask-chip' } : {})}
          >
            <SubtaskChip task={st} isCompleting={nearDone && activeDragId === st.id} isCompact={isCompact} />
          </div>
        ))}
        {/* Add subtask row */}
        <div
          className="absolute left-0 right-0 flex items-center pl-3"
          style={{ top: `${subtasks.length * 44 + 10}px`, height: '30px' }}
          {...(index === 0 ? { 'data-tutorial': 'first-track-add-subtask' } : {})}
        >
          {showAdd ? (
            <form onSubmit={handleAddSubtask} className="flex items-center gap-2" onBlur={(e) => { if (!newTitle && !e.currentTarget.contains(e.relatedTarget as Node)) setShowAdd(false); }}>
              <input
                autoFocus
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setShowAdd(false);
                  if (e.key === 'Enter') { e.preventDefault(); handleAddSubtask(e as any); }
                }}
                placeholder="Subtask name..."
                aria-label="New subtask name"
                maxLength={200}
                className="bg-bg-raised border border-border-default rounded-lg px-2.5 py-1 text-caption text-text-primary
                  placeholder:text-text-muted focus:outline-none focus:border-accent w-44"
              />
              <div className="flex gap-1">
                {(['p1', 'p2', 'p3'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setNewPriority(p)}
                    className={`px-1.5 py-0.5 rounded-md text-micro font-semibold transition-all
                      ${p === 'p1' ? 'bg-p1/15 text-p1' : p === 'p2' ? 'bg-p2/15 text-p2' : 'bg-p3/15 text-p3'}
                      ${newPriority === p ? 'ring-2 ring-offset-1' : 'opacity-40 hover:opacity-70'}
                      ${newPriority === p && p === 'p1' ? 'ring-p1' : newPriority === p && p === 'p2' ? 'ring-p2' : newPriority === p ? 'ring-p3' : ''}`}
                  >
                    {p.toUpperCase()}
                  </button>
                ))}
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-1.5 text-micro text-text-muted hover:text-accent transition-colors"
            >
              <Plus size={12} />
              Add subtask
            </button>
          )}
        </div>
      </div>

      {/* Finish zone */}
      <div className="w-11 shrink-0 border-l border-border-subtle" />
    </div>
    </motion.div>
  );
}
